import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import {
  AnyObject,
  CommonDataSetAction,
  FormFieldConfig,
  PaginateDto,
  TableDataColumn,
  ListDataSetConfig,
  ValueType, ItemAttrListDataSet
} from '@mri/types';
import { toTitleCase } from '@mri/functions';
import { AuthResourceService, GenericResourceService } from '@mri/app/resources';
import { mergeMap, of, Subject, Subscription, tap } from 'rxjs';
import { MatDrawer } from '@angular/material/sidenav';

@Component({
  selector: 'app-table-data-set',
  templateUrl: './table-data-set.component.html',
  styleUrl: './table-data-set.component.scss'
})
export class TableDataSetComponent implements OnInit, OnDestroy {

  private _reload$ = new Subject<PaginateDto>();
  private _subscriptions = new Subscription();
  private _gatewayUrl?: string;

  selectedElements: Set<AnyObject> = new Set();
  filterFields?: FormFieldConfig[];
  actions!: CommonDataSetAction[];
  columns?: TableDataColumn[];
  countFindElements!: number;
  isLoading: boolean;

  @ViewChild('drawerFilter') drawerFilter: MatDrawer;


  @Input() filterValueRecord!: Record<string, ValueType | ValueType[]>;
  @Input() sources: AnyObject[];
  @Input() size: number = 20;

  @Output() filterValueRecordChange = new EventEmitter<Record<string, ValueType | ValueType[]>>();
  @Output() actionListener = new EventEmitter<{ action: string, elements: Array<AnyObject> }>();

  @Input({ required: true })
  set config(config: ListDataSetConfig) {
    const { columns, actions, gatewayUrl, filter } = config;
    this.columns = columns;
    this.actions = actions;
    this.filterFields = filter;
    this._gatewayUrl = gatewayUrl;
  }

  get columnKeys(): string[] {
    return ['$select', ...(this.columns?.map(c => c.key) || []), ...(this.actionsInRow.length ? ['actions'] : [])];
  }

  get selectedElementList(): string[] {
    return [...this.selectedElements];
  }

  get actionsInRow() {
    return this.actions.filter(a => a.inRow);
  }

  get filterCount() {
    // cada valor de filterFields es un array, cuenta la cantidad de elementos en cada array y los suma
    // si filterFields es undefined, retorna 0
    return Object.keys(this.filterValueRecord || {})?.reduce((acc, field) => {
      const value = (this.filterValueRecord || {})[field] || null;
      if (!value) return acc;
      if (Array.isArray(value)) return acc + value.length;
      return acc + 1;
    }, 0) || 0;
  }




  constructor(
    private _genericSrcSrv: GenericResourceService,
    private _authSrv: AuthResourceService
  ) {
  }

  ngOnInit(): void {

    this._subscriptions.add(
      this._reload$
        .pipe(
          mergeMap(paginate => this._reloadData(paginate))
        ).subscribe()
    );

    this._subscriptions.add(
      this._authSrv.auth$
        .pipe(
          tap(() => this.changePage())
        ).subscribe()
    );
  }


  ngOnDestroy() {
    this._subscriptions.unsubscribe();
  }

  getExtraData(element: AnyObject): ItemAttrListDataSet {
    return {
      marks: {},
      element,
      selectedElements: [...this.selectedElements],
      elements: this.sources,
      totalCount: this.countFindElements,
      filterValueRecord: this.filterValueRecord
    }
  }

  onChangeFilterValues(values: Record<string, ValueType | ValueType[]>) {
    this.filterValueRecord = values;
    this.filterValueRecordChange.emit(values);

    this.drawerFilter.close().then();
    this._reload$.next({ page: 1, size: this.size });
  }

  changePage(paginate: PaginateDto = { page: 1, size: this.size }) {
    this._reload$.next(paginate);
  }

  pressAction(action: CommonDataSetAction, elements?: Array<AnyObject>) {
    elements = elements || [...this.selectedElements];
    this.actionListener.next({ action: action.key, elements });

    if(action.click) action.click(this.getExtraData(elements[0]));
  }

  toggleSelect(element: AnyObject) {
    if (this.selectedElements.has(element)) {
      this.selectedElements.delete(element);
      return;
    }

    this.selectedElements.add(element);
  }

  onlySelect(element: AnyObject) {
    this.selectedElements = new Set([element]);
  }

  onVisible(element: AnyObject, isVisible: boolean, index: number) {
    if (isVisible) return this.onShow(element, index);
    this.onHide(element, index);
  }

  onShow(element: AnyObject, index: number) {
    if (this.isLoading) return;
    const currentCount = this.sources.length;

    if (!(currentCount - 10 < index)) return;

    if (this.countFindElements <= currentCount) return;

    const page = parseInt((currentCount / this.size).toString()) + 1;

    this.changePage({ size: this.size, page });
  }

  onHide(element: AnyObject, index: number) {
    console.log({ element,index})
  }

  private _generateExampleSources() {
    const sources = [];
    for (let i = 0; i < 10; i++) {
      const object: AnyObject = {};

      this.columnKeys.forEach(column => object[column] = `${toTitleCase(column)} ${i + 1}`);

      sources.push(object);
    }

    this.sources = sources;
  }

  private _reloadData(paginate: PaginateDto) {
    if (!this._gatewayUrl) return of(null);

    const url = this._gatewayUrl || '';
    const { page } = paginate;
    this.isLoading = true;
    const filter = this.filterValueRecord as AnyObject;

    return this._genericSrcSrv.countElements(url, { filter })
      .pipe(
        tap(response => this.countFindElements = (response.count) || 0),

        mergeMap(() => this._genericSrcSrv.listElements(url, { paginate, filter })),
        tap(({ objects }) =>
          this.sources = page === 1 ? objects : [...this.sources, ...objects]
        ),

        tap(() => this.isLoading = false)
      );
  }

}
